import React, { useState, useEffect } from 'react';
import {
  getTotalRegisterClicks,
  getRegisterClicksForTrackedPages,
  getRegisterClicksByDate
} from '../../utils/registerClickTracking';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import './RegisterClickStats.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const RegisterClickStats = () => {
  const [totalClicks, setTotalClicks] = useState(0);
  const [clicksByPage, setClicksByPage] = useState([]);
  const [clicksByDate, setClicksByDate] = useState({});
  const [chartType, setChartType] = useState('bar');

  useEffect(() => {
    loadData();
    // Refresh data every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [total, pageData, dateData] = await Promise.all([
        getTotalRegisterClicks(),
        getRegisterClicksForTrackedPages(),
        getRegisterClicksByDate()
      ]);
      
      setTotalClicks(total);
      setClicksByPage(pageData);
      
      // Sort dates and get last 30 days
      const sortedDates = Object.keys(dateData).sort((a, b) => {
        return new Date(a) - new Date(b);
      }).slice(-30);
      
      const clicksByDateData = {};
      sortedDates.forEach(date => {
        clicksByDateData[date] = dateData[date];
      });
      
      setClicksByDate(clicksByDateData);
    } catch (error) {
      console.error('Error loading register click data:', error);
    }
  };

  // Page clicks chart data
  const pageChartData = {
    labels: clicksByPage.map(p => {
      const pageNames = {
        'home': 'Home',
        'about': 'About',
        'summit-objective': 'Summit Objective'
      };
      return pageNames[p.page] || p.page;
    }),
    datasets: [
      {
        label: 'Register Now Clicks',
        data: clicksByPage.map(p => p.count),
        backgroundColor: [
          'rgba(220, 38, 38, 0.8)',
          'rgba(102, 126, 234, 0.8)',
          'rgba(50, 205, 50, 0.8)',
        ],
        borderColor: [
          'rgba(220, 38, 38, 1)',
          'rgba(102, 126, 234, 1)',
          'rgba(50, 205, 50, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Clicks over time chart data
  const clicksOverTimeData = {
    labels: Object.keys(clicksByDate),
    datasets: [
      {
        label: 'Register Now Clicks per Day',
        data: Object.values(clicksByDate),
        backgroundColor: 'rgba(220, 38, 38, 0.2)',
        borderColor: 'rgba(220, 38, 38, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Register Now Button Clicks by Page',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Register Now Clicks Over Time (Last 30 Days)',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="register-click-stats-wrapper">
      <div className="stats-header">
        <h3>Register Now Button Clicks</h3>
        <div className="total-clicks">
          <span className="total-label">Total Clicks</span>
          <span className="total-value">{totalClicks}</span>
        </div>
      </div>

      <div className="stats-grid">
        <div className="page-stats">
          <h4>Clicks by Page</h4>
          <div className="page-list">
            {clicksByPage.length === 0 ? (
              <p className="no-data">No clicks recorded yet</p>
            ) : (
              clicksByPage.map((page, index) => {
                const pageNames = {
                  'home': 'Home',
                  'about': 'About',
                  'summit-objective': 'Summit Objective'
                };
                return (
                  <div key={index} className="page-item">
                    <span className="page-name">{pageNames[page.page] || page.page}</span>
                    <span className="page-count">{page.count}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-controls">
            <h4>Clicks by Page</h4>
            <div className="chart-type-buttons">
              <button
                className={chartType === 'bar' ? 'active' : ''}
                onClick={() => setChartType('bar')}
              >
                Bar
              </button>
              <button
                className={chartType === 'line' ? 'active' : ''}
                onClick={() => setChartType('line')}
              >
                Line
              </button>
            </div>
          </div>
          <div className="chart-container">
            {chartType === 'bar' && <Bar data={pageChartData} options={chartOptions} />}
            {chartType === 'line' && <Line data={pageChartData} options={chartOptions} />}
          </div>
        </div>

        <div className="chart-section">
          <h4>Clicks Over Time</h4>
          <div className="chart-container">
            <Line data={clicksOverTimeData} options={lineChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterClickStats;

